import React, { MutableRefObject, useMemo, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import { format } from 'date-fns';
import classNames from 'classnames/bind';
import { conditionClassModifier } from '@surfline/web-common';
import { useUserDateFormat, useUserPermissionStatus } from 'selectors/user';
import type {
  DaySelectorDay as DayDaySelectorDayProps,
  SurfDayHeight,
  RatingItem,
} from 'types/daySelector';
import formatWaveHeight from 'utils/formatWaveHeight';
import { type WindTimestep } from 'types/wind';
import conditionIsFlat from 'utils/conditionIsFlat';
import StarCircle from 'components/Icons/StarCircle';
import type { Condition } from 'types/subregion';
import { useForecastDaysAllowed } from 'utils/forecastDaysAllowed';
import WindHourReading from '../../../WindHourReading/WindHourReading';

import styles from './DaySelectorDay.module.scss';

const cx = classNames.bind(styles);
const getConditionsClassName = (className: string, conditions: string) =>
  cx({
    [className]: true,
    [`sl-conditions-color--${conditionClassModifier(conditions)}-background`]: true,
  });

const getDynamicStateClassName = (className: string, isHovered: boolean, isFocused: boolean) =>
  cx({
    [`${className}Hover`]: isHovered,
    [`${className}Focus`]: isFocused,
  });

interface Props {
  activeRef?: MutableRefObject<HTMLButtonElement | null>;
  data: DayDaySelectorDayProps;
  dateFormat?: 'MDY' | 'DMY';
  dayIndex: number;
  daySummary: Condition;
  handleClick: (index: number) => void;
  handleFocus: (index: number) => void;
  handleHover: (index: number) => void;
  handleBlur: () => void;
  isHovered: boolean;
  isFocused: boolean;
  isSelected: boolean;
  isHistoricView: boolean;
  units: string;
  isTableViewMode: boolean;
}

const DaySelectorDay: React.FC<Props> = ({
  activeRef,
  dayIndex,
  daySummary,
  dateFormat = 'MDY',
  data,
  handleClick,
  handleHover,
  handleFocus,
  handleBlur,
  isFocused,
  isHovered,
  isSelected,
  isHistoricView,
  units,
  isTableViewMode,
}) => {
  const formattedDate = useUserDateFormat() || dateFormat;
  const { hasCoreForecastPermissions } = useUserPermissionStatus();
  const surfRating = data?.rating;
  const surfHeight = data?.surf as SurfDayHeight;
  const surfWind = data?.wind;

  let ratings: Array<RatingItem> | [] = [];
  let wind: Array<WindTimestep | null> | [] = [];

  if (surfRating) ratings = [surfRating.am, surfRating.midday, surfRating.pm];
  if (surfWind) wind = [surfWind.am, surfWind.midday, surfWind.pm];

  const waveHeight = useMemo(() => {
    if (!surfHeight) return '';

    const surfHeightTextVariant = units?.toLowerCase() === 'm' ? 'callout1' : 'headline';

    if (conditionIsFlat(surfHeight.max, units)) {
      return <Typography variant={surfHeightTextVariant}>Flat</Typography>;
    }

    return (
      <Typography
        variant={surfHeightTextVariant}
        color={isSelected || isHovered ? 'secondary' : 'body'}
      >
        {`${surfHeight.min}-${surfHeight.max}`}
        <span
          style={{ fontSize: units?.toLowerCase() === 'm' ? '10px' : '14px' }}
        >{`${formatWaveHeight(units, true)}${surfHeight.plus ? '+' : ''}`}</span>
      </Typography>
    );
  }, [isHovered, isSelected, surfHeight, units]);

  const displayDate = useMemo(() => {
    if (!data?.date) return '';

    if (dayIndex === 0 && !isHistoricView) return 'Today';
    if (dayIndex === 1 && !isHistoricView) return 'Tomorrow';
    if (formattedDate === 'MDY') {
      return format(new Date(data.date), 'iii, M/d');
    }
    return format(new Date(data.date), 'iii, d/M');
  }, [data?.date, dayIndex, formattedDate, isHistoricView]);

  const ariaLabel = useMemo(
    () =>
      surfHeight
        ? `${displayDate} will be ${surfHeight?.min} to ${surfHeight?.max}${units}${
            surfHeight?.plus ? '+' : ''
          }`
        : '',
    [displayDate, surfHeight, units],
  );

  const customArrowConfig = useMemo(
    () => ({
      maxSize: 16,
      minSize: 10,
    }),
    [],
  );

  const { forecastDaysAllowed } = useForecastDaysAllowed({
    type: 'spot',
    isEntitled: hasCoreForecastPermissions,
  });

  const waveHeightLabel = () =>
    hasCoreForecastPermissions || (!hasCoreForecastPermissions && dayIndex < forecastDaysAllowed)
      ? waveHeight
      : null;

  const focusHandler = useCallback(() => {
    handleFocus(dayIndex);
  }, [dayIndex, handleFocus]);

  const hoverHandler = useCallback(() => {
    handleHover(dayIndex);
  }, [dayIndex, handleHover]);

  const clickHandler = useCallback(() => {
    handleClick(dayIndex);
  }, [dayIndex, handleClick]);

  return data?.date ? (
    <button
      data-testid="day-selector-graph-day"
      type="button"
      className={cx(
        'graph-day',
        styles.wrapper,
        getDynamicStateClassName('dynamicClass', isHovered, isFocused),
        {
          [styles.wrapperActiveHover]: isHovered,
        },
      )}
      onClick={clickHandler}
      onMouseOver={hoverHandler}
      onFocus={focusHandler}
      onBlur={handleBlur}
      aria-label={ariaLabel}
      ref={isSelected && activeRef ? activeRef : null}
    >
      <div className={styles.details} data-testid="day-selector-graph-day-details">
        <Box className={styles.starContainer} data-testid="day-selector-graph-day-star-container">
          {daySummary?.dayToWatch && !isHistoricView && (
            <StarCircle
              className={cx({
                [styles.inactive]: !(isHovered || isSelected || isTableViewMode),
              })}
              testId="day-selector-graph-day-star"
            />
          )}
        </Box>
        <Typography
          className={cx([
            isHovered || isSelected || isTableViewMode ? styles.activeText : styles.inactiveText,
          ])}
          variant="callout2"
          component="p"
        >
          {displayDate}
        </Typography>
        {waveHeightLabel()}
      </div>
      <div className={styles.windArrows}>
        {wind?.map((reading: WindTimestep | null, i: number) =>
          reading?.timestamp ? (
            <WindHourReading
              key={reading?.timestamp}
              reading={reading}
              hourReadingWidth={20}
              showDetails={false}
              customArrowConfig={customArrowConfig}
            />
          ) : (
            <div
              className={styles.emptyWindArrow}
              data-testid="empty-wind-arrow"
              key={`${i + 1}`}
            />
          ),
        )}
      </div>
      <div className={styles.conditions}>
        {ratings?.map((rating, i: number) =>
          rating?.key ? (
            <div
              key={`${rating.key}-${i + 1}`}
              data-testid="day-selector-graph-day-bar"
              className={getConditionsClassName('bar', rating.key || '')}
            />
          ) : (
            <div
              className={styles.emptyConditionsBar}
              data-testid="empty-conditions-bar"
              key={`${i + 1}`}
            />
          ),
        )}
      </div>
    </button>
  ) : null;
};

export default DaySelectorDay;
